import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from '../../../context/NotificationContext';   
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const PrivacyPolicyList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [privacyPolicies, setPrivacyPolicies] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} privacy policies`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  const getPrivacyPolicies = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching privacy policies with params:", requestParams);
        const response = await masterService.getPrivacyPolicies(requestParams);
        console.log("Privacy Policies API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const privacyPoliciesData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Privacy Policies data:", privacyPoliciesData);
          console.log("Pagination data:", paginationData);

          // Ensure privacyPoliciesData is an array
          if (Array.isArray(privacyPoliciesData)) {
            setPrivacyPolicies(privacyPoliciesData);
          } else {
            console.error(
              "Privacy Policies data is not an array:",
              privacyPoliciesData
            );
            setPrivacyPolicies([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch privacy policies" });
          setPrivacyPolicies([]);
        }
      } catch (error) {
        console.error("Error fetching privacy policies:", error);
        notify.error({ message: "Failed to fetch privacy policies" });
        setPrivacyPolicies([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load privacy policies data
  useEffect(() => {
    setPageTitle("Privacy Policy");
    getPrivacyPolicies();
  }, [getPrivacyPolicies, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getPrivacyPolicies({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getPrivacyPolicies({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add privacy policy
  const handleAddPrivacyPolicy = () => {
    navigate("/privacy-policy/add/new");
  };

  // Handle edit privacy policy
  const handleEditPrivacyPolicy = (ppId) => {
    navigate(`/privacy-policy/edit/${ppId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Order",
      dataIndex: "pp_order",
      key: "pp_order",
      width: 80,
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.pp_order - b.pp_order,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text}</Text>,
    },
    {
      title: "Heading",
      dataIndex: "pp_heading",
      key: "pp_heading",
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPrivacyPolicy(record.pp_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Privacy Policies ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search privacy policies..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddPrivacyPolicy}
              size="middle"
            >
              Add Privacy Policy
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={privacyPolicies}
          rowKey="pp_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No privacy policies found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default PrivacyPolicyList;
